import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Dumbbell, Coffee, Bean, Mountain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";

const INDUSTRIES = [
  { value: "fitness", label: "Fitness studio / gym" },
  { value: "cafe", label: "Coffee shop / café" },
  { value: "hybrid", label: "Hybrid — fitness + café" },
  { value: "retreat", label: "Retreat operator" },
  { value: "wellness", label: "Wellness / other" },
];

// Solo plan must pick ONE module. Keep in sync with backend SOLO_MODULE_CHOICES.
const SOLO_MODULES = [
  { key: "fitness", name: "Fitness Coaching", blurb: "Clients, workouts, AI programming.", Icon: Dumbbell },
  { key: "cafe", name: "Café Operations", blurb: "POS, kitchen board, menu, tables.", Icon: Coffee },
  { key: "roastery", name: "Coffee Roastery", blurb: "Green stock, roast profiles, wholesale.", Icon: Bean },
  { key: "retreat", name: "Retreat Management", blurb: "Events, bookings, schedules.", Icon: Mountain },
];

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [sp] = useSearchParams();
  const plan = sp.get("plan") || "solo";
  const billingCycle = sp.get("billing_cycle") || (plan === "solo" ? "trial" : "monthly");
  const checkoutToken = sp.get("token") || "";
  const prefillEmail = sp.get("email") || "";
  const prefillName = sp.get("name") || "";

  const [form, setForm] = useState({
    business_name: "",
    owner_name: prefillName,
    email: prefillEmail,
    password: "",
    industry: "hybrid",
  });
  // Solo plan requires the user to pick EXACTLY one module. Hidden for Studio/Empire.
  const [soloModule, setSoloModule] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Redirect to checkout if someone hits /register?plan=studio without a token
  useEffect(() => {
    if (plan === "studio" && !checkoutToken) {
      nav(`/checkout?plan=studio`, { replace: true });
    }
    if (plan === "empire") {
      nav(`/empire-enquiry`, { replace: true });
    }
  }, [plan, checkoutToken, nav]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (plan === "solo" && !soloModule) {
      setError("Please pick one module to start with. You can add more later by upgrading.");
      return;
    }
    setBusy(true);
    try {
      await register({
        ...form,
        plan,
        billing_cycle: billingCycle,
        checkout_token: checkoutToken || undefined,
        solo_module: plan === "solo" ? soloModule : undefined,
      });
      nav("/app");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const planBadge = plan === "studio"
    ? { label: `Studio · ${billingCycle}`, tone: "bg-primary/15 text-primary border-primary/30" }
    : plan === "solo" && billingCycle === "lifetime"
      ? { label: "Solo Lifetime", tone: "bg-primary/15 text-primary border-primary/30" }
      : { label: "Solo · Free 30-day trial", tone: "bg-accent/15 text-accent-foreground border-accent/30" };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1 relative">
        <Link
          to="/"
          data-testid="back-to-site-register"
          className="absolute top-6 left-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <form onSubmit={submit} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg leading-none">K</div>
            <div className="font-display text-xl">Kairosa</div>
          </Link>

          <h1 className="font-display text-4xl mb-2">Start your business.</h1>
          <p className="text-muted-foreground mb-6 text-sm">
            One workspace for coaching, café, retreats and more. 60 seconds to set up.
          </p>

          <div className={"inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] rounded-full border px-3 py-1 mb-6 " + planBadge.tone}>
            {plan === "studio" || (plan === "solo" && billingCycle === "lifetime") ? (
              <ShieldCheck className="h-3 w-3" />
            ) : null}
            <span data-testid="register-plan-badge">{planBadge.label}</span>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Business name</Label>
              <Input
                data-testid={AUTH.regBusiness}
                placeholder="Lumina Wellness Studio"
                value={form.business_name}
                onChange={update("business_name")}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Your name</Label>
              <Input
                data-testid={AUTH.regOwner}
                placeholder="Nadia Okafor"
                value={form.owner_name}
                onChange={update("owner_name")}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid={AUTH.regEmail}
                type="email"
                placeholder="you@brand.com"
                value={form.email}
                onChange={update("email")}
                required
                autoComplete="off"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Password</Label>
              <PasswordInput
                data-testid={AUTH.regPassword}
                testId="register-password-toggle"
                placeholder="Minimum 6 characters"
                value={form.password}
                onChange={update("password")}
                required
                minLength={6}
                autoComplete="new-password"
                containerClassName="mt-1.5"
              />
            </div>
            <div>
              <Label>Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                <SelectTrigger data-testid={AUTH.regIndustry} className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {plan === "solo" && (
              <div data-testid="solo-module-picker">
                <div className="flex items-baseline justify-between">
                  <Label>Pick your module</Label>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Choose 1</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  Solo workspaces run one focused module. Upgrade to Studio anytime to unlock the full stack.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {SOLO_MODULES.map(({ key, name, blurb, Icon }) => {
                    const active = soloModule === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        data-testid={`solo-module-${key}`}
                        onClick={() => setSoloModule(key)}
                        aria-pressed={active}
                        className={
                          "text-left rounded-lg border p-3 transition-all " +
                          (active
                            ? "border-primary bg-primary/5 ring-1 ring-primary/40"
                            : "border-border bg-background hover:border-primary/40")
                        }
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={"h-4 w-4 " + (active ? "text-primary" : "text-muted-foreground")} strokeWidth={1.5} />
                          <span className="text-sm font-medium leading-none">{name}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground leading-snug">{blurb}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {error && <div data-testid={AUTH.registerError} className="text-sm text-destructive">{error}</div>}
            <Button
              data-testid={AUTH.regSubmit}
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-11 mt-2"
            >
              {busy ? "Creating your workspace…" : "Create workspace"}
            </Button>
          </div>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-foreground underline underline-offset-4">Sign in</Link>
          </div>
        </form>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1511081692775-05d0f180a065?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwY2FmZSUyMGludGVyaW9yfGVufDB8fHx8MTc4NDQzMDE2OHww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 h-full w-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[10px] uppercase tracking-[0.28em] mb-3 opacity-80">Ready in seconds</div>
          <div className="font-display text-4xl leading-tight max-w-md">
            A workspace that grows with your brand.
          </div>
        </div>
      </div>
    </div>
  );
}
